import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, LogIn, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { apiJson } from "../lib/api";
import { auth } from "../lib/firebase";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";

export default function Unsubscribe() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenBillingPortal = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate("/login?redirect=/unsubscribe");
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiJson<{ url?: string; error?: string }>("/api/stripe/create-billing-portal-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: currentUser.uid }),
      });

      if (!response.url) {
        throw new Error(response.error || "Billing portal did not return a URL");
      }

      window.location.assign(response.url);
    } catch (error: any) {
      toast.error(error?.message || "Could not open billing portal");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 text-slate-900">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        <div className="text-center">
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5" />
            Quick unsubscribe
          </p>
          <h1 className="text-3xl font-bold tracking-tight">Cancel your subscription in a few clicks</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            We use Stripe’s secure billing portal for cancellations, plan changes, and payment updates.
            Sign in first, then we’ll send you straight there.
          </p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle>Open your billing portal</CardTitle>
            <CardDescription>
              This is the fastest way to unsubscribe. You can cancel immediately or switch plans from the same screen.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 rounded-xl border bg-slate-50 p-4 text-sm text-slate-700 sm:grid-cols-3">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-4 w-4 text-green-600" />
                <span>Secure Stripe-hosted flow</span>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="mt-0.5 h-4 w-4 text-blue-600" />
                <span>Cancel or change plans instantly</span>
              </div>
              <div className="flex items-start gap-3">
                <LogIn className="mt-0.5 h-4 w-4 text-slate-600" />
                <span>Works after Google sign-in</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button onClick={handleOpenBillingPortal} disabled={isLoading} className="w-full sm:w-auto">
                {isLoading ? "Opening billing portal…" : "Go to billing portal"}
              </Button>
              <Button variant="outline" onClick={() => navigate("/pricing")} className="w-full sm:w-auto">
                View plans
              </Button>
            </div>

            <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-4 text-sm leading-6 text-slate-700">
              <strong>Tip:</strong> if you’re already signed in, this button takes you directly to Stripe. If not, you’ll be sent to Google sign-in first and then right back here.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
